import {
  BarChart3, LayoutDashboard, FolderOpen, CheckSquare, GraduationCap, ClipboardList,
  Settings as SettingsIcon, SquarePen, Pencil, Trash2, MoreHorizontal, Pin, PinOff, Folder, FolderPlus,
  FolderInput, FolderMinus, ChevronDown, ChevronRight, ChevronsUpDown, Palette, UserRound, HelpCircle, LogOut, Compass, BookOpen,
  Download, FileText, ShieldCheck, Bug,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ChatHistory, ChatSummary, ChatFolder } from '@/src/hooks/useChatHistory';
import { LINKS } from '@/src/lib/links';
import { Capacitor } from '@capacitor/core';
import type { AppRole } from '@/src/lib/types';
import Sheet from './sheet';
import { useAppColors } from '@/src/lib/theme';
import VenueSwitcher from './venue-switcher';

type PageId = 'chat' | 'dashboard' | 'financials' | 'documents' | 'tasks' | 'training' | 'eod';

interface NavItem { id: PageId; label: string; icon: React.ReactNode; managerOnly?: boolean; financials?: boolean }

const ICON = { size: 18, strokeWidth: 1.75 };
const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard',  label: 'Dashboard',  icon: <LayoutDashboard {...ICON} /> },
  { id: 'financials', label: 'Financials', icon: <BarChart3 {...ICON} />, financials: true },
  { id: 'tasks',      label: 'Tasks',      icon: <CheckSquare {...ICON} /> },
  { id: 'documents',  label: 'Documents',  icon: <FolderOpen {...ICON} /> },
  { id: 'training',   label: 'Training',   icon: <GraduationCap {...ICON} /> },
  { id: 'eod',        label: 'Reports',    icon: <ClipboardList {...ICON} /> },
];

const RECENT_LIMIT = 15;
const FOLDER_LIMIT = 5;

interface LeftSheetProps {
  isOpen: boolean;
  onClose: () => void;
  activeDrawerId: string;
  onSelectDrawer: (id: string) => void;
  onNewChat: () => void;
  userRole: AppRole;
  userName?: string;
  /** Job title, shown under the name (falls back to the access level). */
  userPosition?: string | null;
  venueName?: string;
  /** Opens the Settings page at a section (profile, personalization...). */
  onOpenSettings?: (section?: string) => void;
  onSignOut?: () => void;
  onOpenIntegrations?: () => void;
  /** Saved chats + folders (private to this person). Undefined = chat history not set up yet. */
  history?: ChatHistory;
  currentChatId?: string | null;
  onOpenChat?: (id: string) => void;
  onDeleteChat?: (id: string) => void;
  /** Owners, managers, and anyone given invoice access (head chef, head bartender). */
  canInvoices?: boolean;
  /** Unread Team Chats messages, shown as a badge. */
  teamUnread?: number;
}

// Remembered open/closed sections (per device; purely cosmetic).
function useStoredFlag(key: string, initial: boolean) {
  const [v, setV] = useState<boolean>(() => {
    try { const s = localStorage.getItem(key); return s === null ? initial : s === '1'; } catch { return initial; }
  });
  const set = (next: boolean) => { setV(next); try { localStorage.setItem(key, next ? '1' : '0'); } catch { /* ignore */ } };
  return [v, set] as const;
}

type Menu =
  | { kind: 'chat'; id: string; rect: DOMRect; sub?: 'move' }
  | { kind: 'folder'; id: string; rect: DOMRect };

export default function LeftSheet({
  isOpen, onClose, activeDrawerId, onSelectDrawer, onNewChat, userRole, userName, userPosition, venueName,
  onOpenSettings, onSignOut, history, currentChatId, onOpenChat, onDeleteChat, canInvoices,
}: LeftSheetProps) {
  const c = useAppColors();
  const isManager = userRole === 'owner' || userRole === 'manager';
  const visibleItems = NAV_ITEMS.filter((item) => (item.financials ? isManager || !!canInvoices : !item.managerOnly || isManager));
  const initials = (userName || 'User').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  const [menu, setMenu] = useState<Menu | null>(null);
  const [acct, setAcct] = useState<{ rect: DOMRect; help?: boolean } | null>(null);
  const [renaming, setRenaming] = useState<{ kind: 'chat' | 'folder'; id: string } | null>(null);
  const [renameText, setRenameText] = useState('');
  const [newFolder, setNewFolder] = useState<{ forChat?: string } | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({});
  const [showAllIn, setShowAllIn] = useState<Record<string, boolean>>({});
  const [showAllRecent, setShowAllRecent] = useState(false);
  const [pinnedOpen, setPinnedOpen] = useStoredFlag('opsalo.sidebar.pinned', true);
  const [foldersOpen, setFoldersOpen] = useStoredFlag('opsalo.sidebar.folders', true);
  const [recentsOpen, setRecentsOpen] = useStoredFlag('opsalo.sidebar.recents', true);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const freshChat = activeDrawerId === 'chat' && !currentChatId;
  const chats = history?.chats ?? [];
  const folders = history?.folders ?? [];
  const canFolder = !!history?.foldersAvailable;
  const pinned = chats.filter((ch) => ch.pinned);
  const recents = chats.filter((ch) => !ch.pinned && !ch.folder_id);
  const inFolder = (fid: string) => chats.filter((ch) => ch.folder_id === fid && !ch.pinned);

  const rowHover = {
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => { if (e.currentTarget.dataset.active !== '1') e.currentTarget.style.background = c.iconBg; },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => { if (e.currentTarget.dataset.active !== '1') e.currentTarget.style.background = 'transparent'; },
  };

  const openChat = (id: string) => { onOpenChat?.(id); onClose(); };
  const startRename = (kind: 'chat' | 'folder', id: string, current: string) => { setMenu(null); setRenaming({ kind, id }); setRenameText(current); };
  const commitRename = () => {
    if (!renaming || !history) return;
    const t = renameText.trim();
    if (t) {
      if (renaming.kind === 'chat') void history.rename(renaming.id, t);
      else void history.renameFolder(renaming.id, t);
    }
    setRenaming(null);
  };
  const startNewFolder = (forChat?: string) => { setMenu(null); setFoldersOpen(true); setNewFolder({ forChat }); setNewFolderName(''); };
  const commitNewFolder = async () => {
    if (!history || !newFolder) return;
    const name = newFolderName.trim();
    const forChat = newFolder.forChat;
    setNewFolder(null);
    if (!name) return;
    const id = await history.createFolder(name);
    if (id) {
      setOpenFolders((p) => ({ ...p, [id]: true }));
      if (forChat) void history.moveToFolder(forChat, id);
    }
  };

  // Drag a chat onto a folder (desktop). Dropping on Recents takes it out of its folder.
  const dragProps = (ch: ChatSummary) => canFolder ? {
    draggable: true,
    onDragStart: (e: React.DragEvent) => { e.dataTransfer.setData('text/opsalo-chat', ch.id); e.dataTransfer.effectAllowed = 'move'; },
  } : {};
  const dropProps = (target: string, folderId: string | null) => canFolder ? {
    onDragOver: (e: React.DragEvent) => { if (e.dataTransfer.types.includes('text/opsalo-chat')) { e.preventDefault(); setDropTarget(target); } },
    onDragLeave: () => setDropTarget((t) => (t === target ? null : t)),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault(); setDropTarget(null);
      const id = e.dataTransfer.getData('text/opsalo-chat');
      if (!id || !history) return;
      if (folderId) setOpenFolders((p) => ({ ...p, [folderId]: true }));
      void history.moveToFolder(id, folderId);
    },
  } : {};

  const inputStyle = { background: c.iconBg, color: c.text, border: `1px solid ${c.borderStrong}` };

  const SectionHeader = ({ label, open, onToggle, action }: { label: string; open: boolean; onToggle: () => void; action?: React.ReactNode }) => (
    <div className="group/sec flex items-center justify-between pl-3 pr-1 pt-5 pb-1">
      <button onClick={onToggle} className="flex items-center gap-1 text-[13px] font-medium" style={{ color: c.textDim }}>
        {label}
        <span className={`transition-opacity ${open ? 'opacity-0 group-hover/sec:opacity-100 max-lg:opacity-100' : 'opacity-100'}`}>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
      </button>
      {action}
    </div>
  );

  const ChatRow = ({ ch, indent }: { ch: ChatSummary; indent?: boolean }) => {
    const active = ch.id === currentChatId && activeDrawerId === 'chat';
    const menuOpen = menu?.kind === 'chat' && menu.id === ch.id;
    if (renaming?.kind === 'chat' && renaming.id === ch.id) {
      return (
        <input autoFocus value={renameText} maxLength={80} onChange={(e) => setRenameText(e.target.value)} onBlur={commitRename}
          onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(null); }}
          className={`w-full ${indent ? 'pl-10' : 'pl-3'} pr-3 py-2 rounded-lg text-[14px] outline-none`} style={inputStyle} />
      );
    }
    return (
      <div {...dragProps(ch)} {...rowHover} data-active={active || menuOpen ? '1' : '0'}
        className="group relative flex items-center rounded-lg cursor-pointer"
        style={{ background: active || menuOpen ? c.iconBg : 'transparent' }}
        onClick={() => openChat(ch.id)} title={ch.title}>
        <span className={`flex-1 min-w-0 ${indent ? 'pl-10' : 'pl-3'} pr-8 py-2 text-[14px] truncate`} style={{ color: active ? c.text : c.textDim }}>
          {ch.title}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setMenu(menuOpen ? null : { kind: 'chat', id: ch.id, rect: e.currentTarget.getBoundingClientRect() }); }}
          title="Options"
          className={`absolute right-1 p-1.5 rounded-md ${active || menuOpen ? 'flex' : 'hidden group-hover:flex max-lg:flex'}`}
          style={{ color: c.textDim }}>
          <MoreHorizontal size={16} />
        </button>
      </div>
    );
  };

  return (
    <Sheet isOpen={isOpen} onClose={onClose} side="left">
      <div className="flex flex-col h-full overflow-hidden lg:w-[260px]" style={{ background: c.bg, borderRight: `1px solid ${c.border}` }}>
        <nav className="flex-1 min-h-0 px-2 py-3 overflow-y-auto scrollbar-hide">
          {/* New chat: simple outline button */}
          <button onClick={() => { onNewChat(); onClose(); }}
            onMouseEnter={(e) => { e.currentTarget.style.background = c.iconBg; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = freshChat ? c.iconBg : 'transparent'; }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 mb-3 rounded-xl text-[15px] font-medium transition-colors"
            style={{ color: c.text, border: `1px solid ${c.borderStrong}`, background: freshChat ? c.iconBg : 'transparent' }}>
            <SquarePen size={17} strokeWidth={1.75} />
            New chat
          </button>

          {visibleItems.map((item) => {
            const isActive = activeDrawerId === item.id;
            return (
              <button key={item.id} {...rowHover} data-active={isActive ? '1' : '0'} data-tour={`nav-${item.id}`}
                onClick={() => { onSelectDrawer(item.id); onClose(); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[15px] transition-colors"
                style={{ background: isActive ? c.iconBg : 'transparent', color: isActive ? c.text : c.textDim }}>
                <span style={{ color: isActive ? c.accentSoft : c.iconColor }}>{item.icon}</span>
                <span className="flex-1 text-left">{item.label}</span>
              </button>
            );
          })}

          {history && (
            <>
              {/* Pinned */}
              {pinned.length > 0 && (
                <>
                  <SectionHeader label="Pinned" open={pinnedOpen} onToggle={() => setPinnedOpen(!pinnedOpen)} />
                  {pinnedOpen && <div className="space-y-px">{pinned.map((ch) => <ChatRow key={ch.id} ch={ch} />)}</div>}
                </>
              )}

              {/* Folders */}
              {canFolder && (
                <>
                  <SectionHeader label="Folders" open={foldersOpen} onToggle={() => setFoldersOpen(!foldersOpen)}
                    action={<button onClick={() => startNewFolder()} title="New folder" className="p-1.5 rounded-md" style={{ color: c.textDim }}><FolderPlus size={15} /></button>} />
                  {foldersOpen && (
                    <div className="space-y-px">
                      {newFolder && (
                        <input autoFocus value={newFolderName} maxLength={60} placeholder="Folder name"
                          onChange={(e) => setNewFolderName(e.target.value)} onBlur={() => void commitNewFolder()}
                          onKeyDown={(e) => { if (e.key === 'Enter') void commitNewFolder(); if (e.key === 'Escape') setNewFolder(null); }}
                          className="w-full px-3 py-2 rounded-lg text-[14px] outline-none" style={inputStyle} />
                      )}
                      {folders.length === 0 && !newFolder && (
                        <p className="px-3 py-1.5 text-[14px]" style={{ color: c.textFaint }}>No folders</p>
                      )}
                      {folders.map((f: ChatFolder) => {
                        const items = inFolder(f.id);
                        const expanded = !!openFolders[f.id];
                        const shown = showAllIn[f.id] ? items : items.slice(0, FOLDER_LIMIT);
                        const menuOpen = menu?.kind === 'folder' && menu.id === f.id;
                        const isDrop = dropTarget === f.id;
                        return (
                          <div key={f.id}>
                            {renaming?.kind === 'folder' && renaming.id === f.id ? (
                              <input autoFocus value={renameText} maxLength={60} onChange={(e) => setRenameText(e.target.value)} onBlur={commitRename}
                                onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(null); }}
                                className="w-full px-3 py-2 rounded-lg text-[14px] outline-none" style={inputStyle} />
                            ) : (
                              <div {...rowHover} {...dropProps(f.id, f.id)} data-active={menuOpen || isDrop ? '1' : '0'}
                                className="group relative flex items-center rounded-lg cursor-pointer"
                                style={{ background: isDrop ? c.accentBg : menuOpen ? c.iconBg : 'transparent', outline: isDrop ? `1px solid ${c.accentBorder}` : 'none' }}
                                onClick={() => setOpenFolders((p) => ({ ...p, [f.id]: !expanded }))}>
                                <span className="flex items-center gap-3 flex-1 min-w-0 pl-3 pr-8 py-2 text-[14px]" style={{ color: c.text }}>
                                  {expanded ? <FolderOpen size={17} strokeWidth={1.75} style={{ color: c.iconColor }} /> : <Folder size={17} strokeWidth={1.75} style={{ color: c.iconColor }} />}
                                  <span className="truncate">{f.name}</span>
                                  {!expanded && items.length > 0 && <span className="text-[12px]" style={{ color: c.textFaint }}>{items.length}</span>}
                                </span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setMenu(menuOpen ? null : { kind: 'folder', id: f.id, rect: e.currentTarget.getBoundingClientRect() }); }}
                                  title="Folder options"
                                  className={`absolute right-1 p-1.5 rounded-md ${menuOpen ? 'flex' : 'hidden group-hover:flex max-lg:flex'}`}
                                  style={{ color: c.textDim }}>
                                  <MoreHorizontal size={16} />
                                </button>
                              </div>
                            )}
                            {expanded && (
                              <div className="space-y-px">
                                {items.length === 0 && <p className="pl-10 pr-3 py-1.5 text-[13px]" style={{ color: c.textFaint }}>Empty. Move a chat here from its ••• menu.</p>}
                                {shown.map((ch) => <ChatRow key={ch.id} ch={ch} indent />)}
                                {items.length > FOLDER_LIMIT && (
                                  <button onClick={() => setShowAllIn((p) => ({ ...p, [f.id]: !p[f.id] }))}
                                    className="pl-10 pr-3 py-2 text-[14px]" style={{ color: c.textDim }}>
                                    {showAllIn[f.id] ? 'Show less' : 'Show more'}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Recents */}
              <div {...dropProps('recents', null)} className="rounded-lg"
                style={{ outline: dropTarget === 'recents' ? `1px dashed ${c.accentBorder}` : 'none' }}>
                <SectionHeader label="Recents" open={recentsOpen} onToggle={() => setRecentsOpen(!recentsOpen)} />
                {recentsOpen && (
                  <div className="space-y-px">
                    {recents.length === 0 && <p className="px-3 py-1.5 text-[14px]" style={{ color: c.textFaint }}>No chats yet</p>}
                    {(showAllRecent ? recents : recents.slice(0, RECENT_LIMIT)).map((ch) => <ChatRow key={ch.id} ch={ch} />)}
                    {recents.length > RECENT_LIMIT && (
                      <button onClick={() => setShowAllRecent(!showAllRecent)} className="px-3 py-2 text-[14px]" style={{ color: c.textDim }}>
                        {showAllRecent ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </nav>

        {/* Profile row (opens Settings), like ChatGPT */}
        <div className="flex-shrink-0 px-2 pt-2 flex flex-col gap-2"
          style={{ borderTop: `1px solid ${c.border}`, paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))' }}>
          <button {...rowHover} data-active={acct ? '1' : '0'} data-tour="settings" onClick={(e) => setAcct(acct ? null : { rect: e.currentTarget.getBoundingClientRect() })}
            className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-left" title="Account" aria-haspopup="menu" aria-expanded={!!acct}
            style={{ background: acct ? c.iconBg : 'transparent' }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
              style={{ background: c.accentBg, border: `1px solid ${c.accentBorder}`, color: c.accentSoft }}>
              {initials}
            </div>
            <div className="flex flex-col flex-1 overflow-hidden">
              <span className="text-[14px] font-medium truncate" style={{ color: c.text }}>{userName || 'User'}</span>
              <span className="text-[12px] truncate" style={{ color: c.textFaint }}>{userPosition?.trim() || ({ owner: 'Owner', manager: 'Manager' } as Record<string, string>)[userRole] || 'Team member'}</span>
            </div>
            <ChevronsUpDown size={16} strokeWidth={1.75} style={{ color: c.iconColor }} />
          </button>
          {venueName && <VenueSwitcher venueName={venueName} userRole={userRole} />}
        </div>
      </div>

      {acct && (
        <PopMenu rect={acct.rect} onClose={() => setAcct(null)}>
          {(() => {
            const go = (section?: string) => { setAcct(null); onOpenSettings?.(section); onClose(); };
            const open = (url: string) => { setAcct(null); window.open(url, '_blank', 'noopener,noreferrer'); };
            const line = <div className="my-1" style={{ borderTop: `1px solid ${c.border}` }} />;
            if (acct.help) {
              const native = Capacitor.isNativePlatform();
              return (
                <>
                  <MenuItem icon={<ChevronRight size={15} className="rotate-180" />} label="Back" onClick={() => setAcct({ ...acct, help: false })} />
                  {line}
                  <MenuItem icon={<Compass size={15} />} label="Show me around" onClick={() => { setAcct(null); onClose(); window.dispatchEvent(new CustomEvent('opsalo:onboarding', { detail: { action: 'tour' } })); }} />
                  {LINKS.guide && <MenuItem icon={<BookOpen size={15} />} label="User guide" onClick={() => open(LINKS.guide)} />}
                  {LINKS.app && !native && <MenuItem icon={<Download size={15} />} label="Download the app" onClick={() => open(LINKS.app)} />}
                  {(LINKS.terms || LINKS.privacy || LINKS.supportEmail) && line}
                  {LINKS.terms && <MenuItem icon={<FileText size={15} />} label="Terms of Service" onClick={() => open(LINKS.terms)} />}
                  {LINKS.privacy && <MenuItem icon={<ShieldCheck size={15} />} label="Privacy Policy" onClick={() => open(LINKS.privacy)} />}
                  {LINKS.supportEmail && <MenuItem icon={<Bug size={15} />} label="Report a problem" onClick={() => { setAcct(null); window.location.href = `mailto:${LINKS.supportEmail}?subject=${encodeURIComponent('OpsAlo problem')}`; }} />}
                </>
              );
            }
            return (
              <>
                <button onClick={() => go('profile')} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left"
                  onMouseEnter={(e) => { e.currentTarget.style.background = c.iconBg; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0" style={{ background: c.accentBg, color: c.accentSoft }}>{initials}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14px] font-medium truncate" style={{ color: c.text }}>{userName || 'User'}</span>
                    <span className="block text-[12px] truncate" style={{ color: c.textFaint }}>{userPosition?.trim() || ({ owner: 'Owner', manager: 'Manager' } as Record<string, string>)[userRole] || 'Team member'}</span>
                  </span>
                </button>
                {line}
                <MenuItem icon={<Palette size={15} />} label="Personalization" onClick={() => go('personalization')} />
                <MenuItem icon={<UserRound size={15} />} label="Profile" onClick={() => go('profile')} />
                <MenuItem icon={<SettingsIcon size={15} />} label="Settings" onClick={() => go(isManager ? 'venue' : 'notifications')} />
                {line}
                <MenuItem icon={<HelpCircle size={15} />} label="Help" arrow onClick={() => setAcct({ ...acct, help: true })} />
                {onSignOut && <MenuItem icon={<LogOut size={15} />} label="Log out" onClick={() => { setAcct(null); onSignOut(); }} />}
              </>
            );
          })()}
        </PopMenu>
      )}

      {menu && history && (
        <PopMenu rect={menu.rect} onClose={() => setMenu(null)}>
          {menu.kind === 'chat' ? (() => {
            const ch = chats.find((x) => x.id === menu.id);
            if (!ch) return null;
            if (menu.sub === 'move') {
              return (
                <>
                  <MenuItem icon={<ChevronRight size={15} className="rotate-180" />} label="Back" onClick={() => setMenu({ ...menu, sub: undefined })} />
                  <div className="my-1" style={{ borderTop: `1px solid ${c.border}` }} />
                  {folders.map((f) => (
                    <MenuItem key={f.id} icon={<Folder size={15} />} label={f.name} checked={ch.folder_id === f.id}
                      onClick={() => { setMenu(null); setOpenFolders((p) => ({ ...p, [f.id]: true })); void history.moveToFolder(ch.id, f.id); }} />
                  ))}
                  <MenuItem icon={<FolderPlus size={15} />} label="New folder…" onClick={() => startNewFolder(ch.id)} />
                </>
              );
            }
            return (
              <>
                {canFolder && <MenuItem icon={ch.pinned ? <PinOff size={15} /> : <Pin size={15} />} label={ch.pinned ? 'Unpin' : 'Pin'}
                  onClick={() => { setMenu(null); void history.setPinned(ch.id, !ch.pinned); }} />}
                <MenuItem icon={<Pencil size={15} />} label="Rename" onClick={() => startRename('chat', ch.id, ch.title)} />
                {canFolder && <MenuItem icon={<FolderInput size={15} />} label="Move to folder" arrow onClick={() => setMenu({ ...menu, sub: 'move' })} />}
                {canFolder && ch.folder_id && <MenuItem icon={<FolderMinus size={15} />} label="Remove from folder"
                  onClick={() => { setMenu(null); void history.moveToFolder(ch.id, null); }} />}
                <div className="my-1" style={{ borderTop: `1px solid ${c.border}` }} />
                <MenuItem icon={<Trash2 size={15} />} label="Delete" danger onClick={() => { setMenu(null); onDeleteChat?.(ch.id); }} />
              </>
            );
          })() : (() => {
            const f = folders.find((x) => x.id === menu.id);
            if (!f) return null;
            return (
              <>
                <MenuItem icon={<Pencil size={15} />} label="Rename folder" onClick={() => startRename('folder', f.id, f.name)} />
                <MenuItem icon={<Trash2 size={15} />} label="Delete folder" danger
                  onClick={() => {
                    setMenu(null);
                    if (window.confirm(`Delete the folder "${f.name}"? The chats inside go back to Recents.`)) void history.deleteFolder(f.id);
                  }} />
              </>
            );
          })()}
        </PopMenu>
      )}
    </Sheet>
  );
}

// Small floating menu, rendered on <body> so the sliding panel can't clip or offset it.
function PopMenu({ rect, onClose, children }: { rect: DOMRect; onClose: () => void; children: React.ReactNode }) {
  const c = useAppColors();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: rect.bottom + 4, left: rect.left });
  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    const h = el.offsetHeight, w = el.offsetWidth;
    const top = rect.bottom + 4 + h > window.innerHeight - 8 ? Math.max(8, rect.top - h - 4) : rect.bottom + 4;
    const left = Math.min(Math.max(8, rect.right - w), window.innerWidth - w - 8);
    setPos({ top, left });
  }, [rect, children]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onScroll = () => onClose();
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('resize', onScroll); };
  }, [onClose]);
  return createPortal(
    <>
      <div className="fixed inset-0 z-[1000]" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div ref={ref} className="fixed z-[1001] min-w-[200px] max-w-[260px] max-h-[60vh] overflow-y-auto p-1.5 rounded-xl shadow-2xl"
        style={{ top: pos.top, left: pos.left, background: c.bgElevated, border: `1px solid ${c.borderStrong}` }}>
        {children}
      </div>
    </>,
    document.body,
  );
}

function MenuItem({ icon, label, onClick, danger, arrow, checked }: { icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean; arrow?: boolean; checked?: boolean }) {
  const c = useAppColors();
  return (
    <button onClick={onClick}
      onMouseEnter={(e) => { e.currentTarget.style.background = c.iconBg; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[14px] text-left"
      style={{ color: danger ? c.danger : c.text }}>
      <span style={{ color: danger ? c.danger : c.iconColor }}>{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {checked && <span className="text-[12px]" style={{ color: c.accentSoft }}>✓</span>}
      {arrow && <ChevronRight size={14} style={{ color: c.textFaint }} />}
    </button>
  );
}
